/**
 * Typed client for the ris viewer routes (ris/docs/viewer-api-contract.md).
 * Every call carries the study-scoped viewer ticket as `Authorization: Bearer`.
 */

export type StoredResult = [value: number | string, unit: string, label: string];

export const ANNOTATION_TYPES = [
  'POINT', 'LINE', 'ARROW', 'ELLIPSE', 'RECTANGLE', 'POLYGON', 'ANGLE', 'COBB-ANGLE', 'FEMORAL-ANTEVERSION',
  'BIDIRECTIONAL', 'PARALLEL-DISTANCE', 'CARDIOTHORACIC-INDEX', 'INSALL-SALVATI', 'COXOMETRY', 'SPINE-LABELING', 'TEXT',
] as const;
export type AnnotationType = (typeof ANNOTATION_TYPES)[number];

export interface AnnotationItemIn {
  series_uid: string | null;
  sop_uid: string | null;
  frame: number | null;
  slice: number | null;
  slices_number: number | null;
  modality: string | null;
  type: AnnotationType;
  title: string | null;
  points: number[];
  results: StoredResult[];
}

export interface AnnotationItem extends AnnotationItemIn {
  id: string;
  study_uid: string;
  created_by: string;
  created_at: string;
}

export interface AnnotationSet {
  version: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
  annotations: AnnotationItem[];
}

export interface AnnotationVersion {
  version: number;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  count: number;
  source: string;
}

export interface ViewerSession {
  member_id: string;
  name: string | null;
  org_id: string;
  perms: string[];
  studies: string[];
  expires_at: string;
  organization: { name: string | null; logo: string | null; logo_single: string | null };
  /** True only with a ticket the patient portal issued to the patient (contract §3): the patient layout. */
  patient?: boolean;
  /** True only with a ticket the results portal issued to a referring doctor / convenio user: the referrer layout. */
  referrer?: boolean;
  /** Who opened it, when and for which org (e.g. "Dra. … · 2026-10-28 14:45 UTC · Clínica …"); set only with `referrer`. */
  watermark?: string | null;
}

export interface KeyImageIn {
  series_uid: string;
  sop_uid: string;
  frame: number;
  description?: string | null;
  slice?: number | null;
  series_description?: string | null;
  is_fusion?: boolean;
  back_layer?: Record<string, unknown> | null;
}

export interface KeyImage extends KeyImageIn {
  id: string;
  study_uid: string;
  created_by: string;
  created_at: string;
}

export type PreferenceModule = 'labels' | 'visualization' | 'screens' | 'resolution';

export interface ViewerPreferences {
  modules: Partial<Record<PreferenceModule, Record<string, unknown>>>;
  customized?: boolean;
  [key: string]: unknown;
}

export interface ProtocolViewport {
  source: 'current' | 'prior';
  series_terms: string[];
  window_width: number | null;
  window_center: number | null;
}

/** A ris hanging protocol (viewer workspace, `GET /hanging-protocols`). */
export interface HangingProtocol {
  id: string;
  name: string;
  modality: string;
  rows: number;
  cols: number;
  viewports: ProtocolViewport[];
  priors_count: number;
  sync: Array<'position' | 'zoom' | 'window'>;
  active: boolean;
}

/** 409 on save: someone saved a newer annotation version. */
export class VersionConflict extends Error {
  constructor(readonly latestVersion: number) {
    super(`annotation version conflict (latest ${latestVersion})`);
  }
}

export class RisError extends Error {
  constructor(readonly status: number, readonly detail: string) {
    super(`ris ${status}: ${detail}`);
  }
}

const VIEWER_PREFIX = '/ris/v1/viewer';

export class RisClient {
  constructor(
    /** Origin + path prefix of ris, e.g. `https://ris.example.com` or `` for same-origin. */
    private readonly base: string,
    /** The viewer ticket, or how to read it at request time (it is renewed under a hub session). */
    private readonly ticket: string | null | (() => string | null),
  ) {}

  /** Absolute URL of a ris viewer route (also used for <img src> of the logo). */
  url(path: string): string {
    return `${this.base.replace(/\/$/, '')}${path.startsWith('/ris/') ? path : `${VIEWER_PREFIX}${path}`}`;
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    const ticket = typeof this.ticket === 'function' ? this.ticket() : this.ticket;
    return ticket ? { Authorization: `Bearer ${ticket}`, ...extra } : extra;
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const response = await fetch(this.url(path), {
      ...init,
      credentials: 'same-origin',
      headers: this.headers((init.headers as Record<string, string> | undefined) ?? {}),
    });
    if (response.ok) return response;
    let detail = response.statusText;
    let body: unknown;
    try {
      body = await response.json();
      if (body && typeof body === 'object' && 'detail' in body) detail = String((body as { detail: unknown }).detail);
    } catch {
      // not JSON
    }
    if (response.status === 409 && body && typeof body === 'object' && 'latest_version' in body) {
      throw new VersionConflict(Number((body as { latest_version: unknown }).latest_version));
    }
    throw new RisError(response.status, detail);
  }

  private async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    return (await (await this.request(path, init)).json()) as T;
  }

  private send<T>(method: string, path: string, body: unknown): Promise<T> {
    return this.json<T>(path, { method, body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
  }

  session(): Promise<ViewerSession> {
    return this.json('/session');
  }

  /**
   * Any other ris route with the ticket (e.g. `/ris/v1/ai/…`, typed in src/ris/assistant/api.ts).
   * A JSON `body` is sent as JSON; FormData / Blob bodies go as they are
   * (and only those take `signal`, which aborts the request).
   */
  call<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    if (body === undefined || body instanceof FormData || body instanceof Blob) {
      return this.json<T>(path, { method, body: body as BodyInit | undefined, signal });
    }
    return this.send<T>(method, path, body);
  }

  /**
   * A ris route that answers a Server-Sent Events stream (`/ris/v1/ai/…/stream`, `…/events`), read
   * with fetch so the ticket goes in the Authorization header, never in the URL. Resolves with the
   * response once the status is OK; a refused request throws RisError like `call`.
   */
  stream(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    const init: RequestInit = { method, headers, signal };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return this.request(path, init);
  }

  // --- annotations (study-level versions) ------------------------------------------------------

  annotationVersions(studyUid: string): Promise<AnnotationVersion[]> {
    return this.json(`/studies/${studyUid}/annotation-versions`);
  }

  annotationSet(studyUid: string, version: number | 'latest' = 'latest'): Promise<AnnotationSet> {
    return this.json(`/studies/${studyUid}/annotation-sets/${version}`);
  }

  /** "Guardar": a full snapshot as the next version; throws VersionConflict on a stale base. */
  saveAnnotationSet(studyUid: string, baseVersion: number, annotations: AnnotationItemIn[]): Promise<AnnotationSet> {
    return this.send('POST', `/studies/${studyUid}/annotation-sets`, { base_version: baseVersion, annotations });
  }

  // --- key images --------------------------------------------------------------------------------

  keyImages(studyUid: string): Promise<KeyImage[]> {
    return this.json(`/studies/${studyUid}/key-images`);
  }

  markKeyImage(studyUid: string, body: KeyImageIn): Promise<KeyImage> {
    return this.send('PUT', `/studies/${studyUid}/key-images`, body);
  }

  async unmarkKeyImage(id: string): Promise<void> {
    await this.request(`/key-images/${id}`, { method: 'DELETE' });
  }

  // --- preferences -------------------------------------------------------------------------------

  preferences(): Promise<ViewerPreferences> {
    return this.json('/preferences');
  }

  savePreferences(body: ViewerPreferences): Promise<ViewerPreferences> {
    return this.send('PUT', '/preferences', body);
  }

  resetPreferences(): Promise<ViewerPreferences> {
    return this.json('/preferences', { method: 'DELETE' });
  }

  // --- hanging protocols --------------------------------------------------------------------------

  hangingProtocols(modality?: string): Promise<HangingProtocol[]> {
    return this.json(`/hanging-protocols${modality ? `?modality=${encodeURIComponent(modality)}` : ''}`);
  }

  // --- PACS writes, downloads, report ------------------------------------------------------------

  /** "Captura Dicom": the viewport as PNG, stored in the study as a Secondary Capture. */
  captureViewport(studyUid: string, png: Blob): Promise<{ study_uid: string; stored: number }> {
    return this.json(`/studies/${studyUid}/captures`, { method: 'POST', body: png, headers: { 'Content-Type': 'image/png' } });
  }

  /** "Adjuntar PDF": stored in the study as an Encapsulated PDF series. */
  attachPdf(studyUid: string, pdf: Blob, title: string): Promise<{ study_uid: string; series_uid: string; sop_uid: string }> {
    const query = new URLSearchParams({ title }).toString();
    return this.json(`/studies/${studyUid}/documents?${query}`, { method: 'POST', body: pdf, headers: { 'Content-Type': 'application/pdf' } });
  }

  /** "Descargar Dicom": the study as a ZIP. */
  async studyArchive(studyUid: string): Promise<Blob> {
    return (await this.request(`/studies/${studyUid}/archive`)).blob();
  }

  /** "Reporte": the study's latest report as PDF. */
  async studyReport(studyUid: string): Promise<Blob> {
    return (await this.request(`/studies/${studyUid}/report`)).blob();
  }

  /** Organization logo as a blob URL (the logo route needs the ticket, so <img src> cannot fetch it directly). */
  async logoObjectUrl(path: string): Promise<string> {
    const response = await this.request(path);
    return URL.createObjectURL(await response.blob());
  }
}
